import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { path, pipe, ifElse, isEmpty, pathOr } from 'ramda';
import { Link } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';

import { Page, Row, Column } from 'components/shared/Layout';
import JourneySearchTables from 'components/shared/JourneySearchTables';
import Button from 'components/ui/Button';
import { currencyShape, offersShape, fareShape, userShape } from 'constants/propTypes';
import { DIRECTIONS } from 'types/classes';
import { getExistingTrains } from '../Exchange.utils';
import ExchangeConfirmSelection from '../ExchangeDetails/ExchangeConfirmSelection';

import useAnimalsPerTrainsQuery from 'components/queries/useAnimalsPerTrainsQuery';
import useAssistancePerTrainsQuery from 'components/queries/useAssistancePerTrainsQuery';
import { types } from 'types/passengers';

const getJourneys = pipe(
  path(['journeys']),
  ifElse(
    isEmpty,
    () => null,
    (data) => data
  )
);

const FlowForward = ({ searchResults, bookingStatus, reference, currency, exchangeSegment, user }) => {
  const [animals, { loading: animalsLoading, data: animalsData }] = useAnimalsPerTrainsQuery();
  const [assistance, { loading: assistanceLoading, data: assistanceData }] = useAssistancePerTrainsQuery();

  useEffect(
    () => {
      animals({
        variables: {
          search: searchResults,
          outboundDate: exchangeSegment.direction === 'outbound' ? exchangeSegment.timing.date : null,
          inboundDate: exchangeSegment.direction === 'inbound' ? exchangeSegment.timing.date : null,
          hasDogs: searchResults.passengers?.some(({ type }) => type === types.DOG)
        }
      });

      assistance({
        variables: {
          search: searchResults,
          outboundDate: exchangeSegment.direction === 'outbound' ? exchangeSegment.timing.date : null,
          inboundDate: exchangeSegment.direction === 'inbound' ? exchangeSegment.timing.date : null
        }
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const [selectedFare, setSelectedFare] = useState(null);

  const existingTrains = getExistingTrains({ [exchangeSegment.direction]: exchangeSegment });
  const currentDate = new Date().toString();

  const withIsExistingTrainProp = (item) => ({
    ...item,
    isExistingTrain: () => Boolean(item.fares.find((fare) => fare.legs.find((leg) => leg.serviceName === exchangeSegment.trainNumber)))
  });

  const safeOutboundJourneys = pathOr([], ['outbound', 'journeys'], searchResults);
  const safeInboundJourneys = pathOr([], ['inbound', 'journeys'], searchResults);

  const outboundJourneys = safeOutboundJourneys.length > 0 ? getJourneys(searchResults.outbound).map(withIsExistingTrainProp) : null;

  const inboundJourneys = safeInboundJourneys.length > 0 ? getJourneys(searchResults.inbound).map(withIsExistingTrainProp) : null;

  const outboundExtras =
    exchangeSegment?.direction === DIRECTIONS.OUTBOUND ? exchangeSegment?.extraProducts?.filter((ep) => !ep?.cancelled) : [];
  const inboundExtras =
    exchangeSegment?.direction === DIRECTIONS.INBOUND ? exchangeSegment?.extraProducts?.filter((ep) => !ep?.cancelled) : [];

  let outboundOriginalClasses = exchangeSegment?.direction === DIRECTIONS.OUTBOUND ? [exchangeSegment?.classOfService?.code] : [];
  let inboundOriginalClasses = exchangeSegment?.direction === DIRECTIONS.INBOUND ? [exchangeSegment?.classOfService?.code] : [];

  const journeyInfo = (offer) => ({ originUIC: offer?.origin?.uic, destinationUIC: offer?.destination?.uic });

  return (
    <Page>
      <Row span={14} start={2}>
        <Column start={1} span={8}>
          <Button element={<Link to={`/manage/bookings/${reference}`} />}>
            <FormattedMessage id="exchanges.back" />
          </Button>
        </Column>

        <JourneySearchTables
          isFlowForward
          showFareDifference
          outboundDate={currentDate}
          inboundDate={currentDate}
          outboundInfo={journeyInfo(searchResults.outbound)}
          inboundInfo={journeyInfo(searchResults.inbound)}
          originalOutboundTotal={searchResults.outbound.totalPriceOfOriginalExchangeItems}
          originalInboundTotal={searchResults.inbound.totalPriceOfOriginalExchangeItems}
          outboundJourneys={outboundJourneys}
          inboundJourneys={inboundJourneys}
          selectFare={setSelectedFare}
          currency={currency}
          selectedOutbound={selectedFare}
          selectedInbound={selectedFare}
          isWheelchairSearch={path(['outbound', 'isWheelchair'], searchResults) || path(['inbound', 'isWheelchair'], searchResults)}
          animalsPerTrain={animalsData}
          animalsPerTrainLoading={animalsLoading}
          assistancePerTrain={assistanceData}
          assistancePerTrainLoading={assistanceLoading}
          dogOnlySearch={searchResults.passengers?.every(({ type }) => type === types.DOG)}
          outboundClassesOfService={searchResults.outbound.classesOfService}
          inboundClassesOfService={searchResults.inbound.classesOfService}
        />
        <Column span={3}>
          <ExchangeConfirmSelection
            isStationAgent={user.isStation}
            status={bookingStatus}
            isFlowForward
            outboundOriginalClassesOfAllServices={outboundOriginalClasses}
            inboundOriginalClassesOfAllServices={inboundOriginalClasses}
            data={{
              [`${exchangeSegment.direction}SelectedFare`]: selectedFare,
              reference,
              currency,
              existingTrains,
              outboundExtras,
              inboundExtras
            }}
            disabled={!selectedFare}
          />
        </Column>
      </Row>
    </Page>
  );
};

FlowForward.propTypes = {
  searchResults: offersShape,
  bookingStatus: PropTypes.string,
  reference: PropTypes.string,
  currency: currencyShape,
  exchangeSegment: fareShape,
  user: userShape
};

export default FlowForward;
